import { Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CargoDialogResult } from '@cargo/models/cargo-dialog-result';
import { CargoDialogService } from '@cargo/services/cargo-dialog.service';
import { OrderAdDetailStatus } from '@core/enums/order-ad-detail-status';
import { BackendAPI } from '@core/models/backend-api';
import { OrderAdDetail } from '@core/models/order-ad-detail.model';
import { Order } from '@core/models/order.model';
import { AppConfigService } from '@core/services/app-config.service';
import { Logger } from '@core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { MaterialPreviewDialogParams } from '@order/models/material-preview-dialog-params';
import { MaterialPreviewDialogResult } from '@order/models/material-preview-dialog-result';
import { MaterialPreviewDialogComponent } from '@order/order/material-preview-dialog/material-preview-dialog.component';
import { NewMaterialDialogComponent } from '@order/order/new-material-dialog/new-material-dialog.component';
import { OrderHelperService } from '@order/services/order-helper.service';
import { OrderStateService } from '@order/services/order-state.service';
import { ProductPlacementSizeComboService } from '@order/services/product-placement-size-combo.service';
import { OrderMaterialViewModel } from '@order/viewModels/order-material.viewModel';
import { StatusService } from '@shared/services/status.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { Observable, Subject, from, map, mergeMap, of, toArray } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-order-material-panel',
	templateUrl: './order-material-panel.component.html',
	styleUrls: ['./order-material-panel.component.css'],
})
export class OrderMaterialPanelComponent implements OnDestroy {
	private readonly COMPONENT_NAME = "OrderMaterialPanelComponent";

	protected uniqueOrderAdDetails: OrderAdDetail[] | undefined = undefined;

	protected materials: OrderMaterialViewModel[] = [];

	// Note: the update of the order is performed in two ways:
	// upon initial order set, and when parent component calls update() directly after existing order lines are updated

	@Input()
	set order(order: Order | undefined) {
		this._order = order;
		if (this._order) {
			this.update();
		}
	}
	get order() {
		return this._order;
	}
	private _order: Order | undefined;

	protected orderId: string = "";

	private _isCargoEnabled: boolean = false;

	private _subs = new SubSink();

	constructor (
		public orderHelperService: OrderHelperService,
		private _alertService: UserAlertService,
		private _appConfigService: AppConfigService,
		private _cargoDialogService: CargoDialogService,
		private _dialog: MatDialog,
		private _orderStateService: OrderStateService,
		private _productPlacementSizeComboService: ProductPlacementSizeComboService,
		private _statusService: StatusService,
		private _translate: TranslateService,
		private _logger: Logger
	) {
		this._subs.add(
			this._appConfigService.isCargoEnabled$.subscribe((isCargoEnabled: boolean) => {
				this._isCargoEnabled = isCargoEnabled;
			})
		);
	}

	ngOnDestroy() {
		this._subs.unsubscribe();
	}

	/**
	 * Forces the component to rebuild the material view models and update the view
	 */
	public update(): void {
		this.orderId = this?.order?.orderHeader?.id ?? "";

		if (this.order && this.order.orderAdDetails && this.order.orderAdDetails.length > 0) {
			const uniqueOrderAdDetails = this.getUniqueOrderLinesByMaterialAndEarliestDeadline(this.order.orderAdDetails);

			this.createMaterialViewModels(uniqueOrderAdDetails, this.orderId).subscribe({
				next: (materialVMs: OrderMaterialViewModel[]) => {
					this.materials = materialVMs;
				}
			});
		}
	}

	/**
	 * Displays Material Preview dialog for existing marterials, or New Material Selection Dialog if the material is not uploaded yet
	 * @param materialVM material view model, clicked by the user
	 */
	handlePrintOrderMaterialClick(materialVM: OrderMaterialViewModel): void {
		if (materialVM.isDone) {
			// Spec: if the selected material already exists, then the preview of that material shown

			const materialPreviewDialogParams = new MaterialPreviewDialogParams(materialVM.orderLine, this.orderId, this.order?.orderHeader.advertiserId || "");
			materialPreviewDialogParams.useExternalMaterialUploader = this._isCargoEnabled;

			const matPreviewDialogRef = this._dialog.open(MaterialPreviewDialogComponent,
				{
					data: materialPreviewDialogParams,
					maxWidth: '80vw',
				});

			this._subs.add(
				matPreviewDialogRef.afterClosed().subscribe((newMaterialDialogRes: MaterialPreviewDialogResult) => {
					if (newMaterialDialogRes && newMaterialDialogRes.externalMaterialUploaderRequested && this._isCargoEnabled) {
						// Cargo's Upload Ready Material dialog handles the material upload when user wnats to re-upload it
						this.displayCargoUploadReadyMaterialDialog(materialVM);
					}
				})
			);
		}
		else {
			if (this._orderStateService.hasUnsavedChanges || this._orderStateService.hasUnsavedMaterialChanges) {
				// the order must be saved to add new material
				this._alertService.messageByTranslateKey("ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TEXT", undefined, "ORDER_BASKET.MATERIAL_COMMON.ORDER_UNSAVED_WARNING_TITLE", undefined);
				return;
			}

			this.displayNewMaterialDialog(materialVM);
		}
	}

	/**
	 * Displays the NewMaterialDialogComponent as a dialog. Updates the preview (materialVM.updatedPreviewUrl) and status in case the preview was updated and reported.
	 * Note: as this code does not interacts with Cargo service directly, it needs to use reporter so any consecutive dialog could report that the preview is updated.
	 * @param materialVM
	 */
	private displayNewMaterialDialog(materialVM: OrderMaterialViewModel): void {
		// If there is no existing material, then the dialog shows the following options on clickable cards:
		// - Upload Complete Material
		// - We’ll Design It
		// - Repeat Past Ad

		// The materialReporter will be passed to the dialog and will be called once the material is uploaded and preview is updated
		const materialReporter = new Subject<MaterialPreviewDialogResult>();
		this._subs.add(
			materialReporter.subscribe((newMaterialDialogRes: MaterialPreviewDialogResult) => {
				if (this._isCargoEnabled) {
					// Cargo: new material is uploaded - copy the preview URL and set the status to 'In progress'
					materialVM.updatedPreviewUrl = newMaterialDialogRes.previewUrl;
					this._translate.get("ORDER_BASKET.MATERIAL_PANEL.NEW_MATERIAL_UPLOADED_TEMP_STATUS").subscribe((statusText: string) => materialVM.statusText = statusText);
				}
				else {
					// Cross-ad: new material is uploaded - change the status to 'Ready' and set the isDone flag
					this._translate.get("ORDER_BASKET.STATUS.READY").subscribe((statusText: string) => materialVM.statusText = statusText);
					materialVM.isDone = true;
				}
			}));

		// Open the New Material Dialog and pass the materialReporter as one of the parameters so any consecutive dialogs could report that new material is uploaded
		this._dialog.open(NewMaterialDialogComponent, {
			data: new MaterialPreviewDialogParams(materialVM.orderLine, this.orderId, this.order?.orderHeader.advertiserId || "", materialReporter),
			maxWidth: '80vw'
		});
	}

	/**
	 * Displays the Cargo Upload Ready Material dialog. Updates the preview (materialVM.updatedPreviewUrl) and status in case the preview was updated by Cargo.
	 * Note: as this code interacts with Cargo service directly, it does not need to use reporter to update the preview
	 * @param materialVM
	 */
	private displayCargoUploadReadyMaterialDialog(materialVM: OrderMaterialViewModel): void {
		// CASS-453 & CASS-472 - use parent order id as order id when interfacing with Cargo
		if (!materialVM.orderLine.orderId) {
			this._logger.error(this.COMPONENT_NAME, "displayCargoUploadReadyMaterialDialog(): the order Id in the provided order line is empty");
			return;
		}

		this._cargoDialogService.displayUploadReadyMaterialDialog(
			"ORDER_BASKET.NEW_MATERIAL_OPTIONS_DIALOG.UPLOAD_COMPLETE_MATERIAL_OPTION",
			materialVM.orderLine.orderId,
			materialVM.orderLine.materialId,
			materialVM.orderLine.width,
			materialVM.orderLine.depth
		)
			.subscribe({
				next: (cargoDialogResult: CargoDialogResult | undefined) => {
					// undefined means the dialog was cancelled
					if (cargoDialogResult && cargoDialogResult.previewURL) {
						materialVM.updatedPreviewUrl = cargoDialogResult.previewURL;
						this._translate.get("ORDER_BASKET.MATERIAL_PANEL.NEW_MATERIAL_UPLOADED_TEMP_STATUS").subscribe((statusText: string) => materialVM.statusText = statusText);
					}
				}
			});
	}

	/**
	 * Returns the order lines with unique material Id and having the earliest material deadline for each Id
	 * @param orderLines
	 * @returns array of order lines, one order line per material Id
	 */
	private getUniqueOrderLinesByMaterialAndEarliestDeadline(orderLines: OrderAdDetail[]): OrderAdDetail[] {
		const uniqueOrderLines = new Map<string | null, OrderAdDetail>();

		for (const orderLine of orderLines) {
			const nameKey = orderLine.materialId ? orderLine.materialId : null;
			const existingOrderLine = uniqueOrderLines.get(nameKey);

			if (!existingOrderLine || ((orderLine.materialDeadline || "") < (existingOrderLine.materialDeadline || ""))) {
				uniqueOrderLines.set(nameKey, orderLine);
			}
		}

		return Array.from(uniqueOrderLines.values()).sort((a: OrderAdDetail, b: OrderAdDetail) => (a.materialId || "").localeCompare(b.materialId || ""));
	}

	private getSizeColumnString(data: OrderAdDetail): string {
		const size = this._productPlacementSizeComboService.getFixedSizeByDimensions(data.packageId, data.placementId, data.moduleCode, data.columns, data.depth);
		if (size) {
			return `${size.label}\n${data.width}x${data.depth}`;
		}
		else {
			return `${data.width}x${data.depth}`;
		}
	}

	/**
	 * Creates an array of material view models based on the given order lines.
	 * @param orderLines
	 * @param orderId
	 * @returns 
	 */
	private createMaterialViewModels(orderLines: OrderAdDetail[], orderId: string): Observable<OrderMaterialViewModel[]> {
		return from(orderLines).pipe(
			mergeMap((orderLine: OrderAdDetail) => {

				let statusText$: Observable<string> | undefined;
				let isDoneFallback: boolean = false;

				if (orderLine.materialStatus) {
					statusText$ = this.getStatusTextByCode(orderLine.materialStatus?.code, orderLine.materialStatus?.label);
				}
				else {
					// While BE does not provide material status, the orderLine.statusFlags field is used instead. Consider to remove this code after BE is fixed.
					this._logger.info(this.COMPONENT_NAME, `Order line does not have material status set. Falling back to status flags. Order Id="${orderId}", order line Id="${orderLine.id}"`);
					if (this._statusService.doesAdDetailHaveStatus(orderLine, OrderAdDetailStatus.Produced)) {
						statusText$ = this._translate.get("ORDER_BASKET.STATUS.READY");
						isDoneFallback = true;
					}
					else {
						statusText$ = this._translate.get("ORDER_BASKET.STATUS.REQUIRED");
					}
				}

				return statusText$.pipe(
					map((statusText: string) => {
						const sizeColumnData = this.getSizeColumnString(orderLine);
						const materialVM = new OrderMaterialViewModel(orderLine, this.orderHelperService.composeFullMaterialId(orderLine.orderId, orderLine.materialId), sizeColumnData, statusText);
						materialVM.previewUrl = `${BackendAPI.orderMaterial}/${orderId}/${orderLine.materialId}/thumbnail`;

						if (isDoneFallback) {
							materialVM.isDone = true;
						}

						return materialVM;
					})
				);
			}),
			toArray()
		);
	}

	/**
	 * Looks up the translation string for given status code in "CUSTOM.MATERIAL_STATUS_MAPPING.<statusCode>"
	 * @param statusCode
	 * @param label
	 * @returns translation if present, or label otherwise
	 */
	private getStatusTextByCode(statusCode: string | undefined, label: string | undefined): Observable<string> {
		if (statusCode) {
			const translationKey = `CUSTOM.MATERIAL_STATUS_MAPPING.${statusCode}`;
			return this._translate.get(translationKey).pipe(
				map((translation: string) => {
					// the translator returns the last fragment of the key if the translation is missing
					if (translation !== statusCode) {
						return translation;
					}
					else if (label) {
						return label;
					}
					else {
						this._logger.warning(this.COMPONENT_NAME, `Material status code "${statusCode}" does not have corresponding label nor translation.`);
						return "";
					}
				})
			);
		}
		else {
			this._logger.warning(this.COMPONENT_NAME, `Material status code "${statusCode}" is missing, misconfiguration or bug in the code.`);
			return of("");
		}
	}
}
